(() => {
  'use strict';

  const VERSION = '6.1.0';
  const STORAGE_KEY = 'alfajr_cod_last_order';
  const WINDOW_NAME_PREFIX = 'ALFAJR_COD_ORDER::';
  const config = window.ALFAJR_COD_BRIDGE_CONFIG || {};
  function thankYouUrl() {
    const configured = window.ALFAJR_COD_PUBLIC_SETTINGS?.settings?.successPageUrl || config.thankYouUrl;
    if (typeof configured === 'string' && configured.startsWith('/') && !configured.startsWith('//')) {
      try {
        const target = new URL(configured, location.origin);
        if (target.origin === location.origin) return `${target.pathname}${target.search}`;
      } catch (_) {}
    }
    return '/pages/merci-commande';
  }
  const redirectDelay = Number.isFinite(Number(config.redirectDelay))
    ? Math.max(0, Number(config.redirectDelay))
    : 500;

  if (window.__ALFAJR_COD_BRIDGE_INSTALLED__) return;
  window.__ALFAJR_COD_BRIDGE_INSTALLED__ = true;
  window.ALFAJR_COD_BRIDGE_VERSION = VERSION;

  let activeForm = null;
  let redirectStarted = false;
  let cartSnapshot = null;
  let pendingDraft = null;

  const asText = (value) => String(value ?? '').trim();

  function parseJson(value) {
    try {
      return JSON.parse(value);
    } catch (_) {
      return null;
    }
  }

  function amount(value, fallback = 0) {
    if (value === null || value === undefined || asText(value) === '') return fallback;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const normalized = asText(value)
      .replace(/\u00a0/g, ' ')
      .replace(/\s/g, '')
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '');
    if (!normalized) return fallback;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function orderNumber(value) {
    const normalized = asText(value);
    if (!normalized) return '';
    return normalized.startsWith('#') ? normalized : `#${normalized}`;
  }

  function currentRoot(form = activeForm) {
    return (
      form?.closest?.('[data-alfajr-cod-root]') ||
      document.querySelector('[data-cod-overlay].is-open')?.closest?.('[data-alfajr-cod-root]') ||
      document.querySelector('[data-alfajr-cod-portal] [data-cod-form]')?.closest?.('[data-alfajr-cod-root]') ||
      document
    );
  }

  function currentForm() {
    if (activeForm?.isConnected) return activeForm;
    return (
      document.querySelector('[data-cod-overlay].is-open [data-cod-form]') ||
      document.querySelector('[data-alfajr-cod-portal] [data-cod-form]') ||
      document.querySelector('[data-cod-form]')
    );
  }

  function saveOrder(order) {
    const payload = {
      ...order,
      customer: { ...(order.customer || {}) },
      items: Array.isArray(order.items) ? order.items : [],
      currency: order.currency || 'MAD',
      capturedAt: Date.now(),
      bridgeVersion: VERSION
    };

    const serialized = JSON.stringify(payload);

    try {
      sessionStorage.setItem(STORAGE_KEY, serialized);
    } catch (_) {}

    try {
      localStorage.setItem(STORAGE_KEY, serialized);
    } catch (_) {}

    try {
      window.name = `${WINDOW_NAME_PREFIX}${serialized}`;
    } catch (_) {}

    window.ALFAJR_COD_ORDER = payload;
    window.ALFAJR_COD_BRIDGE_LAST_ORDER = payload;

    try {
      document.dispatchEvent(new CustomEvent('alfajr_cod_bridge_saved', { detail: payload }));
    } catch (_) {}

    return payload;
  }

  function readSavedOrder() {
    let raw = '';
    try {
      raw = sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (_) {}

    if (!raw) {
      try {
        raw = localStorage.getItem(STORAGE_KEY) || '';
      } catch (_) {}
    }

    if (!raw && asText(window.name).startsWith(WINDOW_NAME_PREFIX)) {
      raw = asText(window.name).slice(WINDOW_NAME_PREFIX.length);
    }

    return parseJson(raw) || {};
  }

  function merge(base = {}, incoming = {}) {
    const output = { ...base, ...incoming };
    output.customer = { ...(base.customer || {}), ...(incoming.customer || {}) };

    const incomingItems = Array.isArray(incoming.items) ? incoming.items : [];
    const baseItems = Array.isArray(base.items) ? base.items : [];
    output.items = incomingItems.length ? incomingItems : baseItems;

    for (const key of ['orderId', 'orderNumber', 'subtotal', 'shipping', 'total', 'currency']) {
      const value = incoming[key];
      if ((value === '' || value === null || value === undefined) && base[key] !== undefined) {
        output[key] = base[key];
      }
    }

    return output;
  }

  function requestIsCod(input, init = {}) {
    const rawUrl = typeof input === 'string' ? input : input?.url || '';
    let pathname = rawUrl;

    try {
      pathname = new URL(rawUrl, location.origin).pathname;
    } catch (_) {}

    const method = asText(init.method || input?.method || 'GET').toUpperCase();
    return method === 'POST' && (
      pathname === '/apps/alfajr-cod/api/cod' ||
      pathname.endsWith('/api/cod') ||
      pathname.includes('/alfajr-cod/api/cod')
    );
  }

  async function requestPayload(input, init = {}) {
    const body = init.body;

    if (typeof body === 'string') return parseJson(body) || {};

    if (body instanceof URLSearchParams) {
      return Object.fromEntries(body.entries());
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return Object.fromEntries(body.entries());
    }

    if (typeof Request !== 'undefined' && input instanceof Request) {
      try {
        return parseJson(await input.clone().text()) || {};
      } catch (_) {
        return {};
      }
    }

    return {};
  }

  function productTitle(root) {
    return asText(
      root.querySelector('[data-cod-product-card] .alfajr-cod-v2__product-copy > strong')?.textContent ||
      document.querySelector('meta[property="og:title"]')?.content ||
      document.querySelector('h1')?.textContent ||
      'Article AL FAJR'
    );
  }

  function singleItem(root, payload) {
    const form = currentForm();
    const quantity = Math.max(1, amount(payload.quantity ?? form?.elements?.quantity?.value, 1));
    const unitPrice = amount(root.querySelector('[data-cod-unit-price]')?.textContent, 0);
    const displayedSubtotal = amount(root.querySelector('[data-cod-subtotal]')?.textContent, unitPrice * quantity);

    return {
      id: asText(payload.variantId || form?.elements?.variant_id?.value || new URLSearchParams(location.search).get('variant')),
      title: productTitle(root),
      variant: asText(root.querySelector('[data-cod-variant-title]')?.textContent),
      quantity,
      price: unitPrice || (quantity ? displayedSubtotal / quantity : displayedSubtotal),
      image: asText(
        root.querySelector('[data-cod-product-image]')?.currentSrc ||
        root.querySelector('[data-cod-product-image]')?.src ||
        document.querySelector('meta[property="og:image"]')?.content
      ),
      url: location.href
    };
  }

  function cartItems(root, payload) {
    const requestedItems = Array.isArray(payload.items) ? payload.items : [];
    const rows = Array.from(root.querySelectorAll('[data-cod-cart-products] .alfajr-cod-v2__product'));

    return requestedItems.map((requested, index) => {
      const row = rows[index];
      const quantity = Math.max(1, amount(requested.quantity, 1));
      const lineTotal = amount(row?.querySelector('.alfajr-cod-v2__cart-line-total')?.textContent, null);

      return {
        id: asText(requested.variantId),
        title: asText(row?.querySelector('.alfajr-cod-v2__product-copy > strong')?.textContent) || `Article ${index + 1}`,
        variant: asText(row?.querySelector('.alfajr-cod-v2__variant b')?.textContent),
        quantity,
        price: lineTotal === null ? null : lineTotal / quantity,
        image: asText(row?.querySelector('img')?.currentSrc || row?.querySelector('img')?.src),
        url: location.href
      };
    });
  }

  function matchingCartSnapshot(payload) {
    const items = cartSnapshot?.order?.items;
    if (!payload.idempotencyKey || payload.idempotencyKey !== cartSnapshot?.requestId) return null;
    if (!Array.isArray(items) || !Array.isArray(payload.items) || items.length !== payload.items.length) return null;
    if (!items.every((item, index) =>
      asText(item.id) === asText(payload.items[index].variantId) &&
      Number(item.quantity) === Number(payload.items[index].quantity)
    )) return null;
    return { ...cartSnapshot.order, items: items.map((item) => ({ ...item })) };
  }

  function draftFromPage(payload = {}) {
    const form = currentForm();
    if (form) activeForm = form;
    const root = currentRoot(form);
    const snapshot = matchingCartSnapshot(payload);

    const customer = {
      name: asText(payload.fullName || form?.elements?.full_name?.value),
      phone: asText(payload.phone || form?.elements?.phone?.value),
      city: asText(payload.city || form?.elements?.city?.value),
      address: asText(payload.address || form?.elements?.address?.value)
    };

    const items = snapshot?.items || (Array.isArray(payload.items) && payload.items.length
      ? cartItems(root, payload)
      : [singleItem(root, payload)]);

    const calculatedSubtotal = items.reduce((sum, item) => sum + amount(item.price) * amount(item.quantity, 1), 0);
    const subtotal = amount(snapshot?.subtotal ?? root.querySelector('[data-cod-subtotal]')?.textContent, calculatedSubtotal);
    const shipping = amount(snapshot?.shipping ?? root.querySelector('[data-cod-shipping]')?.textContent, 0);
    const total = amount(snapshot?.total ?? root.querySelector('[data-cod-total]')?.textContent, subtotal + shipping);

    return {
      customer,
      items,
      subtotal,
      shipping,
      total,
      currency: 'MAD'
    };
  }

  function completedOrder(draft, result) {
    const total = amount(result?.order?.total, draft.total || 0);
    const shipping = amount(result?.shippingFee, draft.shipping || 0);
    const subtotal = Math.max(0, total - shipping);

    return merge(draft, {
      orderId: asText(result?.order?.id),
      orderNumber: orderNumber(result?.order?.name),
      subtotal,
      shipping,
      total,
      currency: asText(result?.order?.currency) || 'MAD',
      meta: {
        browserEnabled: result?.meta?.browserEnabled === true,
        pixelId: asText(result?.meta?.pixelId),
        eventId: asText(result?.meta?.eventId)
      }
    });
  }

  function isThankYouPage() {
    try {
      const currentPath = new URL(location.href).pathname.replace(/\/+$/, '');
      const thankPath = new URL(thankYouUrl(), location.origin).pathname.replace(/\/+$/, '');
      return currentPath === thankPath;
    } catch (_) {
      return location.pathname.replace(/\/+$/, '') === '/pages/merci-commande';
    }
  }

  function redirect(order) {
    if (redirectStarted || isThankYouPage()) return;
    redirectStarted = true;

    const saved = saveOrder(order);
    const target = new URL(thankYouUrl(), location.origin);

    if (saved.orderNumber) target.searchParams.set('order', saved.orderNumber.replace(/^#/, ''));
    if (saved.orderId) target.searchParams.set('order_id', saved.orderId);
    if (Number.isFinite(Number(saved.subtotal))) target.searchParams.set('subtotal', String(saved.subtotal));
    if (Number.isFinite(Number(saved.shipping))) target.searchParams.set('shipping', String(saved.shipping));
    if (Number.isFinite(Number(saved.total))) target.searchParams.set('total', String(saved.total));

    window.setTimeout(() => {
      if (!isThankYouPage()) location.assign(`${target.pathname}${target.search}`);
    }, redirectDelay);
  }

  function installSubmitCapture() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-cod-form]')) return;
      activeForm = form;
      saveOrder(merge(readSavedOrder(), draftFromPage({})));
    }, true);
  }

  function installEventFallbacks() {
    document.addEventListener('alfajr_cod_cart_snapshot', (event) => {
      const detail = event.detail;
      if (!detail?.requestId || !Array.isArray(detail.order?.items)) return;
      cartSnapshot = {
        requestId: detail.requestId,
        order: { ...detail.order, items: detail.order.items.map((item) => ({ ...item })) }
      };
    }, true);

    document.addEventListener('alfajr_cod_purchase', (event) => {
      if (redirectStarted) return;
      const detail = event.detail || {};
      const draft = pendingDraft || draftFromPage({});
      pendingDraft = saveOrder(merge(draft, {
        orderId: asText(detail.order_id),
        orderNumber: orderNumber(detail.order_name),
        shipping: amount(detail.shipping_fee, draft.shipping || 0),
        total: amount(detail.value, draft.total || 0),
        subtotal: Math.max(0, amount(detail.value, draft.total || 0) - amount(detail.shipping_fee, draft.shipping || 0)),
        currency: asText(detail.currency) || 'MAD'
      }));
    }, true);

    document.addEventListener('cod_order_success', (event) => {
      if (redirectStarted) return;
      const detail = event.detail || {};
      const draft = pendingDraft || draftFromPage({});
      const total = amount(detail.value, draft.total || 0);
      const shipping = amount(detail.shippingFee, draft.shipping || 0);
      const order = merge(draft, {
        orderNumber: orderNumber(detail.orderName),
        subtotal: Math.max(0, total - shipping),
        shipping,
        total,
        currency: asText(detail.currency) || 'MAD'
      });
      saveOrder(order);
      redirect(order);
    }, true);
  }

  function installFetchInterceptor() {
    const originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    window.fetch = async function alfajrCodFetch(input, init = {}) {
      if (!requestIsCod(input, init)) {
        return originalFetch.apply(this, arguments);
      }

      const payload = await requestPayload(input, init);
      const draft = draftFromPage(payload);
      pendingDraft = draft;
      saveOrder(draft);

      const response = await originalFetch.apply(this, arguments);

      // Parse and save the successful API response before returning it to the
      // COD form. This guarantees that an immediate app redirect cannot outrun
      // local/session storage.
      let result = null;
      try {
        result = await response.clone().json();
      } catch (_) {}

      if (response.ok && result?.success && result?.order) {
        const order = completedOrder(draft, result);
        saveOrder(order);
        redirect(order);
      }

      return response;
    };
  }

  if (!isThankYouPage()) {
    installSubmitCapture();
    installEventFallbacks();
    installFetchInterceptor();
  }
})();
